import { type FormModelField, FormModelFieldSubForm, PoleFieldIds } from '@ikegps/core-shared';
import type { FormDesigner } from '../../form.designer';
import { m, type MithrilVNode } from '../../../../mithril';
import { type SelectInputOption, SelectInputState } from '../../../../components/input/input.select';
import { IkeInputStateGroup } from '../../../../components/input/input.state.group';
import { MenuComponent } from '../../../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../../../components/menu/menu.item';
import { action, makeObservable, observable } from 'mobx';
import { T, Translate } from '../../../../i18n';
import { MobxState } from '../../../../state/mobx';
import { addStyles } from '../../../../components/theme';
import { SubformEditorDropdown } from './form.field.editor.subform.dropdown';
import { SubFormTypeEditorCSS, Style } from './form.field.editor.subform.style';
import type { FormFieldSelectList } from '@ikegps/core-api';

addStyles(Style);

export class SpanSubFormTypeEditor {
	private vectorFieldSelectState: SelectInputState;
	private typeFieldSelectState: SelectInputState;
	private environmentFieldSelectState: SelectInputState;
	private midSpanPhotoFieldSelectState: SelectInputState;

	private vectorMenu: MenuComponent;
	private typeMenu: MenuComponent;
	private environmentMenu: MenuComponent;
	private midSpanPhotoMenu: MenuComponent;

	private reactions: MobxState;
	private _allowCreateNewEnvironmentField: boolean = false;
	@observable private _environmentNoneSelected: boolean = false;

	constructor(
		private field: FormModelFieldSubForm,
		private stateGroup: IkeInputStateGroup,
		private designer?: FormDesigner
	) {
		makeObservable(this);
		this.reactions = new MobxState();

		const spanState = this.field.subFormSpanTypeState;

		// 1. Create all four select states with current options and values
		this.vectorFieldSelectState = this.createFieldSelect(T.SPAN_VECTOR_FIELD, this.vectorFieldOptions());
		this.vectorFieldSelectState.setValue(spanState?.vectorFieldId ?? null);
		this.typeFieldSelectState = this.createFieldSelect(T.SPAN_TYPE_FIELD, this.typeFieldOptions());
		this.typeFieldSelectState.setValue(spanState?.typeFieldId ?? null);
		this.environmentFieldSelectState = this.createFieldSelect(T.SPAN_ENVIRONMENT_FIELD, this.environmentFieldOptions());
		this.environmentFieldSelectState.setValue(spanState?.environmentFieldId ?? null);
		this.midSpanPhotoFieldSelectState = this.createFieldSelect(
			T.SPAN_MID_SPAN_IKE_PHOTO_FIELD,
			this.midSpanPhotoFieldOptions()
		);
		this.midSpanPhotoFieldSelectState.setValue(spanState?.midSpanIkePhotoFieldId ?? null);

		// 2. Required states auto-select their first option; env/photo are not auto-selected
		this.vectorFieldSelectState.isRequired = true;
		this.typeFieldSelectState.isRequired = true;

		// 3. Create menus driven by the select states' options
		this.vectorMenu = this.createMenu(this.vectorFieldSelectState);
		this.typeMenu = this.createMenu(this.typeFieldSelectState);
		this.environmentMenu = this.createMenu(this.environmentFieldSelectState);
		this.midSpanPhotoMenu = this.createMenu(this.midSpanPhotoFieldSelectState);

		// 4. Register field-change reactions that update options when subform fields change
		this.registerOptionReaction(() => this.vectorFieldOptions(), this.vectorFieldSelectState);
		this.registerOptionReaction(() => this.typeFieldOptions(), this.typeFieldSelectState);
		this.registerOptionReaction(() => this.environmentFieldOptions(), this.environmentFieldSelectState);
		this.registerOptionReaction(() => this.midSpanPhotoFieldOptions(), this.midSpanPhotoFieldSelectState);
		this.reactions.reaction(
			() =>
				this.field.fields
					.filter((f) => this.field.spanValidator.isEligibleEnvironmentField(f))
					.map((f) => {
						return JSON.stringify((f as unknown as FormFieldSelectList).options);
					})
					.join(','),
			() => {
				this.updateAllowCreateNewEnvironmentField();
			}
		);
		this.updateAllowCreateNewEnvironmentField();

		// 5. Sync broken-state validation onto the select states (reddens the tree row and blocks save)
		this.reactions.reaction(
			() => this.validationSnapshot(),
			() => this.syncValidationErrors()
		);
		this.syncValidationErrors();

		// 6. Register onValue handlers — fire when the user changes a dropdown
		this.vectorFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.span.selectVectorField(value);
			}
		});
		this.typeFieldSelectState.onValue((value) => {
			if (value) {
				this.field.Mutator.span.selectSpanTypeField(value);
			}
		});
		this.environmentFieldSelectState.onValue((value) => {
			if (value) {
				this._environmentNoneSelected = false;
				this.environmentFieldSelectState.setError(null);
				this.field.Mutator.span.selectEnvironmentField(value);
			} else {
				this.field.Mutator.span.clearEnvironmentField();
			}
		});
		this.midSpanPhotoFieldSelectState.onValue((value) => {
			if (value) {
				this.midSpanPhotoFieldSelectState.setError(null);
				this.field.Mutator.span.selectMidSpanIkePhotoField(value);
			}
		});
	}

	private createMenu(state: SelectInputState): MenuComponent {
		return new MenuComponent({
			items: (): MenuItem[] =>
				state.options.map((opt) => ({
					view: MenuItemComponent.view([opt.key]),
					action: () => state.setValue(opt.value),
				})),
		});
	}

	private createFieldSelect(title: string, options: SelectInputOption[]): SelectInputState {
		const state = new SelectInputState(Translate(title), options);
		this.stateGroup.add(state);
		return state;
	}

	private registerOptionReaction(options: () => SelectInputOption[], state: SelectInputState) {
		this.reactions.reaction(
			() =>
				options()
					.map((opt) => `${opt.value}:${opt.key}`)
					.join(','),
			() => this.updateFieldOptions(options(), state)
		);
	}

	@action private updateFieldOptions(options: SelectInputOption[], state: SelectInputState) {
		state.setOptions(options);

		if (!options.find((opt) => opt.value === state.value)) {
			state.setValue(null);
		}
	}

	/**
	 * Eligible fields plus the currently assigned field when it still exists but
	 * fails eligibility — a broken assignment must stay visible, not silently clear.
	 */
	private buildFieldOptions(
		isEligible: (field: FormModelField) => boolean,
		assignedId: string | null | undefined
	): SelectInputOption[] {
		const options = this.field.fields.filter(isEligible).map((field) => ({ key: field.displayName, value: field.id }));
		if (assignedId && !options.some((opt) => opt.value === assignedId)) {
			const assigned = this.field.getOptionalField(assignedId);
			if (assigned) {
				options.push({ key: assigned.displayName, value: assigned.id });
			}
		}
		return options;
	}

	private vectorFieldOptions(): SelectInputOption[] {
		return this.buildFieldOptions(
			(f) => this.field.spanValidator.isEligibleVectorField(f),
			this.field.subFormSpanTypeState?.vectorFieldId
		);
	}

	private typeFieldOptions(): SelectInputOption[] {
		return this.buildFieldOptions(
			(f) => this.field.spanValidator.isEligibleTypeField(f),
			this.field.subFormSpanTypeState?.typeFieldId
		);
	}

	private environmentFieldOptions(): SelectInputOption[] {
		return this.buildFieldOptions(
			(f) => this.field.spanValidator.isEligibleEnvironmentField(f),
			this.field.subFormSpanTypeState?.environmentFieldId
		);
	}

	private midSpanPhotoFieldOptions(): SelectInputOption[] {
		return this.buildFieldOptions(
			(f) => this.field.spanValidator.isEligibleMidSpanPhotoField(f),
			this.field.subFormSpanTypeState?.midSpanIkePhotoFieldId
		);
	}

	private validationSnapshot(): string {
		const validator = this.field.spanValidator;
		return [
			validator.vectorStatus,
			validator.typeStatus,
			validator.environmentStatus,
			validator.midSpanPhotoStatus,
			validator.missingOrWrongRoles.join('|'),
		].join(',');
	}

	@action private syncValidationErrors() {
		const validator = this.field.spanValidator;

		this.applyRoleError(this.vectorFieldSelectState, validator.vectorStatus, null);
		this.applyRoleError(
			this.typeFieldSelectState,
			validator.typeStatus,
			validator.isTypeFieldRepairable ? Translate(T.SPAN_TYPE_BROKEN) : Translate(T.SPAN_FIELD_BROKEN)
		);
		this.applyRoleError(this.environmentFieldSelectState, validator.environmentStatus, null);
		if (validator.midSpanPhotoStatus === 'unassigned') {
			this.midSpanPhotoFieldSelectState.setError(Translate(T.INPUT_REQUIRED));
			this.midSpanPhotoFieldSelectState.setTouched();
		} else {
			this.applyRoleError(this.midSpanPhotoFieldSelectState, validator.midSpanPhotoStatus, null);
		}
	}

	private applyRoleError(state: SelectInputState, status: string, brokenError: string | null) {
		if (status === 'missingField' || status === 'invalidField') {
			state.setError(brokenError ?? Translate(T.SPAN_FIELD_BROKEN));
			state.setTouched();
		} else {
			state.setError(null);
		}
	}

	allowCreateNewEnvironmentField(): boolean {
		return this._allowCreateNewEnvironmentField;
	}

	@action private updateAllowCreateNewEnvironmentField() {
		const validator = this.field.spanValidator;
		const fieldWithEnvFieldId = this.field.fields.filter(
			(field) => field.id === PoleFieldIds.SPAN.ENVIRONMENT && validator.isEligibleEnvironmentField(field)
		);
		const validDefaultEnvAvailable = this.field.fields.find((field) => validator.isDefaultEnvironmentField(field));
		this._allowCreateNewEnvironmentField = fieldWithEnvFieldId.length === 0 && !validDefaultEnvAvailable;
	}

	hasNoValidMidSpanPhotoOptions(): boolean {
		return this.field.fields.filter((f) => this.field.spanValidator.isEligibleMidSpanPhotoField(f)).length === 0;
	}

	@action private selectEnvironmentNone() {
		this._environmentNoneSelected = true;
		this.environmentFieldSelectState.setValue(null);
	}

	@action createEnvironmentField() {
		const typeIndex = this.field.fields.findIndex((f) => this.field.spanValidator.isEligibleTypeField(f));
		const insertIndex = typeIndex >= 0 ? typeIndex : 2;
		const field = this.field.Mutator.span.addEnvironmentField(insertIndex);

		this.environmentFieldSelectState.setOptions(this.environmentFieldOptions());
		this.environmentFieldSelectState.setValue(field.id);
		this.environmentFieldSelectState.setError(null);
		this.field.Mutator.span.selectEnvironmentField(field.id);
	}

	@action createMidSpanPhotoField() {
		const field = this.field.Mutator.span.addMidSpanPhotoField();

		this.midSpanPhotoFieldSelectState.setOptions(this.midSpanPhotoFieldOptions());
		this.midSpanPhotoFieldSelectState.setValue(field.id);
		this.midSpanPhotoFieldSelectState.setError(null);
	}

	@action repairTypeField() {
		const typeFieldId = this.field.subFormSpanTypeState?.typeFieldId;

		this.field.Mutator.span.repairSpanTypeField();

		this.updateFieldOptions(this.typeFieldOptions(), this.typeFieldSelectState);
		if (typeFieldId) {
			this.typeFieldSelectState.setValue(typeFieldId);
			// Drop the cached select list editor so its input states rebuild from the repaired options
			this.designer?.clearCachedEditor(this.field.id + ':' + typeFieldId);
		}
	}

	view(): MithrilVNode {
		const selectedText = (state: SelectInputState): string => {
			const opt = state.options.find((o) => o.value === state.value);
			return opt?.key ?? Translate(T.SELECT_OPTION);
		};

		const stateError = (state: SelectInputState): string | null =>
			state.touched && !state.isValid && state.hasError() ? Translate(state.error) : null;

		return m('div', { className: SubFormTypeEditorCSS.wrapper }, [
			m('hr'),
			m('div', { className: SubFormTypeEditorCSS.grid }, [
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_VECTOR_FIELD),
					selectedText: selectedText(this.vectorFieldSelectState),
					menu: this.vectorMenu,
					error: stateError(this.vectorFieldSelectState),
				}),
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_TYPE_FIELD),
					selectedText: selectedText(this.typeFieldSelectState),
					menu: this.typeMenu,
					error: stateError(this.typeFieldSelectState),
					actionBtn: this.field.spanValidator.isTypeFieldRepairable
						? { action: () => this.repairTypeField(), btnText: Translate(T.FORM_FIELD_REPAIR), accent: true }
						: undefined,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_ENVIRONMENT_FIELD),
					selectedText: this._environmentNoneSelected
						? Translate(T.NONE)
						: selectedText(this.environmentFieldSelectState),
					menu: this.environmentMenu,
					error: stateError(this.environmentFieldSelectState),
					allowNone: true,
					noneSelected: this._environmentNoneSelected,
					onNoneSelect: () => this.selectEnvironmentNone(),
					actionBtn:
						this.allowCreateNewEnvironmentField() || this._environmentNoneSelected
							? { action: () => this.createEnvironmentField(), btnText: Translate(T.FORM_FIELD_CREATE) }
							: undefined,
				}),
				SubformEditorDropdown.view({
					title: Translate(T.SPAN_MID_SPAN_IKE_PHOTO_FIELD),
					selectedText: selectedText(this.midSpanPhotoFieldSelectState),
					menu: this.midSpanPhotoMenu,
					error: stateError(this.midSpanPhotoFieldSelectState),
					actionBtn: this.hasNoValidMidSpanPhotoOptions()
						? { action: () => this.createMidSpanPhotoField(), btnText: Translate(T.FORM_FIELD_CREATE) }
						: undefined,
				}),
			]),
		]);
	}

	destroy() {
		this.reactions.destroy();

		// Remove select states from the parent state group
		this.stateGroup.remove(this.vectorFieldSelectState);
		this.stateGroup.remove(this.typeFieldSelectState);
		this.stateGroup.remove(this.environmentFieldSelectState);
		this.stateGroup.remove(this.midSpanPhotoFieldSelectState);
	}
}
